import { useEffect, useRef, useSyncExternalStore } from 'react';
import { api } from './api';

/**
 * Desktop notifications (operator, 2026-10-22: "for the web notifications we
 * need to add browser or use os notification center some times user didn't get
 * the notifications on only system").
 *
 * Each notification also shows in the computer's own notification center —
 * Windows' or the Mac's — carried by the browser's push service (Web Push):
 * with or without a page of the system open, as long as the browser is running.
 * The service worker (public/sw.js) shows it and, clicked, opens its record. A
 * browser with no push service (Brave, unless told otherwise) still shows them,
 * from a page of the system while one is open.
 *
 * On or off is this browser's, like the bell's sound, and the browser asks the
 * person first. Signing out takes the browser off the server's list; the next
 * sign-in there puts it back, for whoever that is.
 *
 * The same file is in admin and sys: each is its own site, with its own
 * permission and its own subscription.
 */

/** unsupported — this browser cannot; blocked — the person told the browser no; off; on. */
export type DesktopState = 'unsupported' | 'blocked' | 'off' | 'on';
/** While on: carried by the push service, or — in a browser without one — shown by an open page. */
export type DesktopMode = 'push' | 'page';

export interface DesktopSnapshot {
  state: DesktopState;
  mode: DesktopMode | null;
  /** Never asked in this browser, and not switched off: the prompt offers them. */
  askable: boolean;
  busy: boolean;
}

const OFF_KEY = 'viganium.desktopNotifications'; // 'off' = switched off in this browser
const PROMPT_KEY = 'viganium.desktopNotificationsPrompt'; // 'later' = put off until the next sign-in
const SHOWN_KEY = 'viganium.notificationShownAt'; // the newest notification a page put on the desktop itself
const ICON = '/notification-icon.png';

/** Messages the service worker posts to the pages. */
const ARRIVED = 'viganium:notification';
const OPENED = 'viganium:notification-open';

// Storage can be unavailable (a locked-down browser): the switch then lasts the page view.
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Not remembered, then.
  }
}

export function desktopSupported(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext && 'Notification' in window && 'serviceWorker' in navigator;
}

const pushSupported = () => 'PushManager' in window;
const switchedOff = () => read(OFF_KEY) === 'off';

// ---------------------------------------------------------------------------
// One state for the page: the bell's switch and the prompt read the same.
// ---------------------------------------------------------------------------

let snapshot: DesktopSnapshot = first();
const listeners = new Set<() => void>();

function first(): DesktopSnapshot {
  if (!desktopSupported()) return { state: 'unsupported', mode: null, askable: false, busy: false };
  const permission = Notification.permission;
  return {
    state: permission === 'denied' ? 'blocked' : permission === 'granted' && !switchedOff() ? 'on' : 'off',
    mode: null,
    askable: false,
    busy: false,
  };
}

function set(next: Partial<DesktopSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  for (const listener of listeners) listener();
}

function listen(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Where things stand now, outside React. */
function settled(): Pick<DesktopSnapshot, 'state' | 'mode' | 'askable'> {
  const permission = Notification.permission;
  if (permission === 'denied') return { state: 'blocked', mode: null, askable: false };
  if (permission !== 'granted' || switchedOff()) {
    return { state: 'off', mode: null, askable: permission === 'default' && !switchedOff() };
  }
  return { state: 'on', mode: snapshot.mode, askable: false };
}

// ---------------------------------------------------------------------------
// The service worker and the push subscription.
// ---------------------------------------------------------------------------

let registering: Promise<ServiceWorkerRegistration> | null = null;

/** The service worker, registered once a page load. */
function worker(): Promise<ServiceWorkerRegistration> {
  registering ??= navigator.serviceWorker
    .register('/sw.js')
    .then(() => navigator.serviceWorker.ready)
    .catch((e: unknown) => {
      registering = null;
      throw e;
    });
  return registering;
}

/**
 * What a click on Turn on needs at hand: Firefox and Safari ask the person only
 * inside the click itself, so the subscribing must not wait for anything first.
 */
let ready: { registration: ServiceWorkerRegistration; key: Uint8Array<ArrayBuffer> } | null = null;

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(text.length / 4) * 4, '=');
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

function sameKey(held: ArrayBuffer | null, key: Uint8Array): boolean {
  if (!held || held.byteLength !== key.length) return false;
  const bytes = new Uint8Array(held);
  return bytes.every((b, i) => b === key[i]);
}

/**
 * Subscribes this browser to its push service — once — and tells the server,
 * every page load, that it still has it. False when it has no push service:
 * an open page shows the notifications instead.
 */
async function pushTo(registration: ServiceWorkerRegistration): Promise<boolean> {
  if (!ready) return false;
  try {
    let subscription = await registration.pushManager.getSubscription();
    if (subscription && !sameKey(subscription.options.applicationServerKey, ready.key)) {
      // Made for another server key: nothing can reach it any more, here or on the server's list.
      await api.del('/notifications/push/subscriptions', { endpoint: subscription.endpoint }).catch(() => undefined);
      await subscription.unsubscribe();
      subscription = null;
    }
    subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: ready.key });
    await api.post('/notifications/push/subscriptions', subscription.toJSON());
    return true;
  } catch {
    return false;
  }
}

/**
 * Takes this browser off the server's list and out of its push service. With
 * `tellServer` false — the session is over — the push service's "gone" tells
 * the server instead, the next time it sends.
 */
async function dropSubscription(tellServer: boolean): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager?.getSubscription();
    if (!subscription) return;
    if (tellServer) {
      await api.del('/notifications/push/subscriptions', { endpoint: subscription.endpoint }).catch(() => undefined);
    }
    await subscription.unsubscribe();
  } catch {
    // Nothing to take back.
  }
}

let syncing: Promise<void> | null = null;

/** Brings this browser in line with its switch, for whoever is signed in. */
function sync(): Promise<void> {
  syncing ??= align().finally(() => {
    syncing = null;
  });
  return syncing;
}

async function align(): Promise<void> {
  if (!desktopSupported()) return set({ state: 'unsupported', mode: null, askable: false });
  const registration = await worker().catch(() => null);
  if (!registration) return set({ state: 'unsupported', mode: null, askable: false });
  if (pushSupported()) {
    try {
      const { publicKey } = await api.get<{ publicKey: string }>('/notifications/push/key');
      ready = { registration, key: fromBase64Url(publicKey) };
    } catch {
      ready = null;
    }
  }
  const now = settled();
  if (now.state !== 'on') {
    // Switched off here: nothing stays on the server for this browser.
    if (Notification.permission === 'granted') await dropSubscription(true);
    return set(now);
  }
  set({ ...now, mode: (await pushTo(registration)) ? 'push' : 'page' });
}

// ---------------------------------------------------------------------------
// The switch.
// ---------------------------------------------------------------------------

/** What turning them on shows, from the page — where the push service cannot carry the server's own. */
export interface DesktopSample {
  title: string;
  body: string;
}

/** Turn on — from a click: the browser asks the person, then one is shown so they see where they appear. */
export async function turnOn(sample: DesktopSample): Promise<void> {
  if (!desktopSupported()) return;
  write(OFF_KEY, null);
  set({ busy: true });
  try {
    if (Notification.permission === 'default') {
      // Asked inside the click. With a push service, subscribing is the question;
      // a refusal or a closed question is the person's answer.
      let answered = false;
      if (ready) {
        await ready.registration.pushManager
          .subscribe({ userVisibleOnly: true, applicationServerKey: ready.key })
          .catch((e: unknown) => {
            answered = e instanceof DOMException && e.name === 'NotAllowedError';
          });
      }
      if (!answered && Notification.permission === 'default') await Notification.requestPermission();
    }
    if (Notification.permission !== 'granted') return set(settled());

    const registration = await worker();
    let pushed = await pushTo(registration);
    if (pushed) {
      // "Turning it on plays it once", as the chime does — sent the whole way round.
      const subscription = await registration.pushManager.getSubscription();
      pushed = subscription
        ? await api.post('/notifications/push/sample', { endpoint: subscription.endpoint }).then(
            () => true,
            () => false,
          )
        : false;
      // The push service would not take it: this browser shows them from its pages instead.
      if (!pushed) await dropSubscription(true);
    }
    set({ state: 'on', mode: pushed ? 'push' : 'page', askable: false });
    if (!pushed) {
      await registration.showNotification(sample.title, {
        body: sample.body,
        tag: 'viganium-sample',
        icon: ICON,
        data: { id: null, link: null },
      });
    }
  } catch {
    set(settled());
  } finally {
    set({ busy: false });
  }
}

/** Turn off — this browser shows nothing more on the desktop. */
export async function turnOff(): Promise<void> {
  write(OFF_KEY, 'off');
  set({ busy: true });
  try {
    await dropSubscription(true);
  } finally {
    set({ ...settled(), busy: false });
  }
}

/**
 * The switch's state, for the bell and the prompt. While someone is signed in,
 * the browser is brought in line once a page load, and again when another tab
 * flips the switch or the browser's permission changes.
 */
export function useDesktopNotifications(userId: string | undefined): DesktopSnapshot {
  const current = useSyncExternalStore(listen, () => snapshot);
  useEffect(() => {
    if (!userId || !desktopSupported()) return;
    void sync();
    const onStorage = (e: StorageEvent) => {
      if (e.key === OFF_KEY || e.key === null) void sync();
    };
    window.addEventListener('storage', onStorage);
    let permission: PermissionStatus | null = null;
    void navigator.permissions
      ?.query({ name: 'notifications' })
      .then((status) => {
        permission = status;
        status.onchange = () => void sync();
      })
      .catch(() => undefined);
    return () => {
      window.removeEventListener('storage', onStorage);
      if (permission) permission.onchange = null;
    };
  }, [userId]);
  return current;
}

// ---------------------------------------------------------------------------
// The prompt: put off until the next sign-in.
// ---------------------------------------------------------------------------

export const promptPutOff = () => read(PROMPT_KEY) === 'later';
export const putOffPrompt = () => write(PROMPT_KEY, 'later');
export const resetDesktopPrompt = () => write(PROMPT_KEY, null);

// ---------------------------------------------------------------------------
// Signing out.
// ---------------------------------------------------------------------------

/** Signing out: this browser stops showing the person's notifications. Called while the session can still say so. */
export async function forgetDesktopNotifications(): Promise<void> {
  if (!desktopSupported()) return;
  await dropSubscription(true);
  await closeShown();
}

/** The session ended without a sign-out — it expired: the browser lets go of its subscription itself. */
export function dropDesktopNotificationsLocally(): void {
  if (desktopSupported()) void dropSubscription(false);
}

// ---------------------------------------------------------------------------
// What the bell does with them.
// ---------------------------------------------------------------------------

/**
 * Takes notifications read in the bell off the desktop as well — those ids, or
 * every notification of the bell's. The sample stays until it is closed.
 */
export async function closeShown(ids?: string[]): Promise<void> {
  if (!desktopSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    for (const shown of (await registration?.getNotifications()) ?? []) {
      const id = (shown.data as { id?: string | null } | null)?.id;
      if (id && (!ids || ids.includes(id))) shown.close();
    }
  } catch {
    // Nothing shown.
  }
}

/** What the service worker tells the pages: one arrived; one was clicked, and this page is to open it. */
export function useWorkerMessages(handlers: {
  arrived: () => void;
  opened: (id: string | null, link: string | null) => void;
}): void {
  const latest = useRef(handlers);
  latest.current = handlers;
  useEffect(() => {
    if (!desktopSupported()) return;
    const container = navigator.serviceWorker;
    const onMessage = (e: MessageEvent) => {
      const message = e.data as { type?: string; id?: string | null; link?: string | null } | null;
      if (message?.type === ARRIVED) latest.current.arrived();
      else if (message?.type === OPENED) latest.current.opened(message.id ?? null, message.link ?? null);
    };
    container.addEventListener('message', onMessage);
    container.startMessages();
    return () => container.removeEventListener('message', onMessage);
  }, []);
}

interface PageNotice {
  id: string;
  title: string;
  titleAr: string | null;
  body: string | null;
  bodyAr: string | null;
  link: string | null;
  createdAt: string;
}

/**
 * In a browser with no push service, the page puts each new notification on
 * the desktop itself, as the bell chimes: once across tabs, never for what was
 * waiting when the page opened, and only while the switch is on. `latestAt` is
 * the bell's — undefined until it has asked since the page opened.
 */
export function useDesktopFromPage(latestAt: string | null | undefined, locale: 'en' | 'ar'): void {
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (latestAt === undefined) return;
    const at = latestAt ? Date.parse(latestAt) : 0;
    const before = seen.current;
    if (before !== null && at <= before) return;
    seen.current = at;
    if (before === null || snapshot.state !== 'on' || snapshot.mode !== 'page') return;
    // Another tab of this site has put it up already.
    if (Number(read(SHOWN_KEY)) >= at) return;
    write(SHOWN_KEY, String(at));
    void showFromPage(before, locale);
  }, [latestAt, locale]);
}

async function showFromPage(after: number, locale: 'en' | 'ar'): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    const unread = await api.get<PageNotice[]>('/notifications', { unreadOnly: true });
    const ar = locale === 'ar';
    // The few newest, oldest of them first, as they would have arrived.
    for (const notice of unread.filter((n) => Date.parse(n.createdAt) > after).slice(0, 5).reverse()) {
      const options: NotificationOptions & { renotify: boolean; timestamp: number } = {
        body: (ar && notice.bodyAr) || notice.body || '',
        tag: notice.id,
        renotify: true,
        icon: ICON,
        lang: locale,
        dir: ar ? 'rtl' : 'ltr',
        timestamp: Date.parse(notice.createdAt),
        data: { id: notice.id, link: notice.link },
      };
      await registration.showNotification((ar && notice.titleAr) || notice.title, options);
    }
  } catch {
    // The bell still has them.
  }
}
